/**
 * Página de chat.
 *
 * Layout adaptativo:
 * - agent / tenant_admin: sidebar (lista de rooms) + conversación.
 * - customer: SIN sidebar — su única room ocupa todo el ancho. Si abre /chat
 *   sin id, lo redirigimos automáticamente a su única conversación.
 *
 * Features:
 * - Avatares + nombres en burbujas ajenas.
 * - Header con info del room + miembros.
 * - Optimistic UI con reconcile por client_msg_uuid.
 * - Marca de leído automática del último mensaje no propio.
 */
import { useEffect, useMemo, useRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { messagesApi, type Message } from "@/api/messages";
import { roomsApi } from "@/api/rooms";
import { useAuthStore } from "@/stores/auth";
import { useWSStore } from "@/stores/ws";
import { RoomList } from "@/components/RoomList";
import { MessageBubble } from "@/components/MessageBubble";
import { MessageInput } from "@/components/MessageInput";
import { ChatHeader } from "@/components/ChatHeader";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function Chat() {
  const { roomId } = useParams<{ roomId: string }>();
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const isCustomer = me?.role === "customer";

  // Customer sin roomId: redirigir a su única room.
  const myRoomsQuery = useQuery({
    queryKey: ["my-rooms"],
    queryFn: roomsApi.myRooms,
  });

  useEffect(() => {
    if (!isCustomer || roomId || !myRoomsQuery.data) return;
    const first = myRoomsQuery.data[0];
    if (first) navigate(`/chat/${first.id}`, { replace: true });
  }, [isCustomer, roomId, myRoomsQuery.data, navigate]);

  const { data: messages, isLoading: msgsLoading } = useQuery({
    queryKey: ["messages", roomId],
    queryFn: () => messagesApi.list(roomId!, { limit: 50 }),
    enabled: Boolean(roomId),
  });

  const { data: members } = useQuery({
    queryKey: ["room-members", roomId],
    queryFn: () => roomsApi.members(roomId!),
    enabled: Boolean(roomId),
  });

  const membersById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof members>[number]>();
    (members ?? []).forEach((m) => map.set(m.id, m));
    return map;
  }, [members]);

  // Scroll al fondo al cambiar de mensajes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Mark as read del último mensaje no propio.
  useEffect(() => {
    if (!messages || !me || !roomId) return;
    const lastNotMine = [...messages].reverse().find((m) => m.sender_user_id !== me.id && m.id > 0);
    if (lastNotMine) {
      messagesApi.markRead(roomId, lastNotMine.id).then(() => {
        qc.invalidateQueries({ queryKey: ["my-rooms"] });
      }).catch(() => {});
    }
  }, [messages, me, roomId, qc]);

  const sendMutation = useMutation({
    mutationFn: async (input: {
      type: Message["type"];
      content_text?: string;
      attachment_id?: string;
    }) => {
      if (!roomId) throw new Error("Sin room");
      const cmid = uuid();
      const optimistic: Message = {
        id: -Date.now(),
        room_id: roomId,
        sender_user_id: me!.id,
        client_msg_uuid: cmid,
        type: input.type,
        content_text: input.content_text ?? null,
        attachment_id: input.attachment_id ?? null,
        created_at: new Date().toISOString(),
        deleted_at: null,
        _status: "sending",
      };
      qc.setQueryData<Message[]>(["messages", roomId], (old) => [...(old ?? []), optimistic]);
      try {
        const server = await messagesApi.send(roomId, {
          client_msg_uuid: cmid,
          type: input.type,
          content_text: input.content_text,
          attachment_id: input.attachment_id,
        });
        qc.setQueryData<Message[]>(["messages", roomId], (old) =>
          (old ?? []).map((m) => (m.client_msg_uuid === cmid ? { ...server, _status: "sent" } : m)),
        );
        qc.invalidateQueries({ queryKey: ["my-rooms"] });
        return server;
      } catch (err) {
        qc.setQueryData<Message[]>(["messages", roomId], (old) =>
          (old ?? []).map((m) => (m.client_msg_uuid === cmid ? { ...m, _status: "failed" } : m)),
        );
        throw err;
      }
    },
  });

  const orderedMessages = useMemo(() => {
    if (!messages) return [];
    const seen = new Set<string>();
    const out: Message[] = [];
    for (const m of messages) {
      if (seen.has(m.client_msg_uuid)) continue;
      seen.add(m.client_msg_uuid);
      out.push(m);
    }
    return out.sort((a, b) => (a.id || 0) - (b.id || 0));
  }, [messages]);

  const isMuted = me?.tenant_status === "muted";
  const showSidebar = !isCustomer;

  // Super admin no debería estar acá — redirigimos.
  if (me?.role === "superadmin") {
    return <Navigate to="/admin/tenants" replace />;
  }

  const activeRoom = myRoomsQuery.data?.find((r) => r.id === roomId);
  const typingState = useWSStore((s) => (roomId ? s.typingByRoom[roomId] : undefined));
  const typingUserIds = (typingState ?? []).map((t) => t.userId).filter((id) => id !== me?.id);

  const editMut = useMutation({
    mutationFn: ({ messageId, text }: { messageId: number; text: string }) =>
      messagesApi.edit(roomId!, messageId, text),
    onSuccess: (updated) => {
      qc.setQueryData<Message[]>(["messages", roomId], (old) =>
        (old ?? []).map((m) =>
          m.id === updated.id ? { ...m, content_text: updated.content_text } : m,
        ),
      );
    },
  });

  const deleteMut = useMutation({
    mutationFn: (messageId: number) => messagesApi.remove(roomId!, messageId),
    onSuccess: (_, messageId) => {
      qc.setQueryData<Message[]>(["messages", roomId], (old) =>
        (old ?? []).map((m) =>
          m.id === messageId ? { ...m, deleted_at: new Date().toISOString() } : m,
        ),
      );
    },
  });

  const replyInputRef = useRef<((text: string) => void) | null>(null);
  const handleReply = (msg: Message) => {
    const sender = membersById.get(msg.sender_user_id);
    const senderName = sender?.full_name ?? "Alguien";
    const quoted = (msg.content_text ?? "").slice(0, 200).split("\n").map((l) => `> ${l}`).join("\n");
    const prefilled = `${quoted}\n— ${senderName}\n\n`;
    replyInputRef.current?.(prefilled);
  };

  return (
    <div className="flex h-full">
      {showSidebar && <RoomList selectedRoomId={roomId} />}
      <div className="flex min-h-0 flex-1 flex-col">
        {!roomId && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-slate-50 text-center text-slate-500">
            <div className="text-4xl">›</div>
            <p>Elegí una conversación de la izquierda para empezar.</p>
          </div>
        )}
        {roomId && (
          <>
            {activeRoom && (
              <ChatHeader
                room={activeRoom}
                members={members ?? []}
                typingUserIds={typingUserIds}
              />
            )}
            <div
              ref={scrollRef}
              className="min-h-0 flex-1 space-y-0 overflow-y-auto bg-slate-50 px-4 py-3"
            >
              {msgsLoading && <div className="text-sm text-slate-500">Cargando mensajes…</div>}
              {orderedMessages.map((m, i) => {
                const prev = orderedMessages[i - 1];
                const next = orderedMessages[i + 1];
                const sameSenderPrev =
                  prev && prev.sender_user_id === m.sender_user_id &&
                  Math.abs(new Date(m.created_at).getTime() - new Date(prev.created_at).getTime()) < 5 * 60 * 1000;
                const sameSenderNext =
                  next && next.sender_user_id === m.sender_user_id &&
                  Math.abs(new Date(next.created_at).getTime() - new Date(m.created_at).getTime()) < 5 * 60 * 1000;
                return (
                  <MessageBubble
                    key={`${m.client_msg_uuid}-${m.id}`}
                    message={m}
                    sender={membersById.get(m.sender_user_id) ?? null}
                    isOwn={m.sender_user_id === me?.id}
                    groupWithPrev={Boolean(sameSenderPrev)}
                    groupWithNext={Boolean(sameSenderNext)}
                    onRetry={
                      m._status === "failed"
                        ? () => sendMutation.mutate({
                            type: m.type,
                            content_text: m.content_text ?? undefined,
                            attachment_id: m.attachment_id ?? undefined,
                          })
                        : undefined
                    }
                    onReply={m.type === "text" && !m.deleted_at ? () => handleReply(m) : undefined}
                    onEdit={
                      m.sender_user_id === me?.id && m.type === "text" && !m.deleted_at && m.id > 0
                        ? () => {
                            const next = window.prompt("Editar mensaje:", m.content_text ?? "");
                            if (next && next.trim() && next !== m.content_text) {
                              editMut.mutate({ messageId: m.id, text: next.trim() });
                            }
                          }
                        : undefined
                    }
                    onDelete={
                      m.sender_user_id === me?.id && !m.deleted_at && m.id > 0
                        ? () => {
                            if (window.confirm("¿Eliminar este mensaje?")) {
                              deleteMut.mutate(m.id);
                            }
                          }
                        : undefined
                    }
                  />
                );
              })}
              {orderedMessages.length === 0 && !msgsLoading && (
                <div className="py-12 text-center text-sm text-slate-500">
                  Sin mensajes todavía. Saludá.
                </div>
              )}
            </div>
            <MessageInput
              roomId={roomId}
              disabled={isMuted}
              disabledReason={isMuted ? "Cuenta pausada — no se pueden enviar mensajes." : undefined}
              onSend={(p) => sendMutation.mutate(p)}
              registerPrefill={(fn) => {
                replyInputRef.current = fn;
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
